import { useEffect, type RefObject } from "react";

function prefersReducedMotion(): boolean {
  if (typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function readScale(shell: HTMLElement | null, scaleFactor: number): number {
  if (shell?.style.zoom) {
    const parsed = parseFloat(shell.style.zoom);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }
  return scaleFactor > 0 ? scaleFactor : 1;
}

function clearPinnedFooter(footer: HTMLElement): void {
  footer.style.position = "";
  footer.style.left = "";
  footer.style.bottom = "";
  footer.style.width = "";
  footer.style.zIndex = "";
}

function readFooterHeight(footer: HTMLElement): number {
  return Math.ceil(Math.max(footer.scrollHeight, footer.offsetHeight));
}

function updateFooterReveal(
  shell: HTMLElement | null,
  content: HTMLElement,
  footer: HTMLElement,
  scaleFactor: number,
): void {
  clearPinnedFooter(footer);

  const scale = readScale(shell, scaleFactor);
  const vh = window.innerHeight;
  const contentRect = content.getBoundingClientRect();
  const footerHeight = readFooterHeight(footer);
  const remainingScreen = contentRect.bottom - vh;
  const offsetLocal = Math.max(0, Math.min(footerHeight, remainingScreen / scale));

  footer.style.transform = `translateY(${offsetLocal}px)`;
}

/** Revelado progresivo del pie + margin-bottom dinámico del content-wrap. */
export function useFooterReveal(
  shellRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  placeholderRef: RefObject<HTMLElement | null>,
  footerRef: RefObject<HTMLElement | null>,
  scaleFactor: number,
) {
  useEffect(() => {
    const shell = shellRef.current;
    const content = contentRef.current;
    const placeholder = placeholderRef.current;
    const footer = footerRef.current;
    if (!content || !placeholder || !footer) return;

    let raf = 0;
    let reservedHeight = 0;

    const syncMargin = () => {
      const height = readFooterHeight(footer);
      if (height === reservedHeight) return;

      reservedHeight = height;
      placeholder.style.minHeight = `${height}px`;
      content.style.marginBottom = `${height}px`;
    };

    const updateReveal = () => {
      if (prefersReducedMotion()) {
        clearPinnedFooter(footer);
        footer.style.transform = "";
        return;
      }

      updateFooterReveal(shell, content, footer, scaleFactor);
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        syncMargin();
        updateReveal();
      });
    };

    syncMargin();
    updateReveal();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(onScrollOrResize);
      ro.observe(footer);
      ro.observe(content);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      ro?.disconnect();
      content.style.marginBottom = "";
      placeholder.style.minHeight = "";
      clearPinnedFooter(footer);
      footer.style.transform = "";
    };
  }, [shellRef, contentRef, placeholderRef, footerRef, scaleFactor]);
}
